import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../atoms/Button";
import { moneyFormat } from "../../services/currencyService";
import InputItem from "../atoms/InputItem";
import { getRedirectUrl } from "../../services/paymentService";
import styled, { css } from "styled-components";
import InputRadio from "../atoms/InputRadio";
import { color } from "../templates/ui";
import ArrowRight from "../atoms/icons/ArrowRight";
import SelectItem from "../atoms/SelectItem";
import Cross from "../atoms/icons/Cross";
import Paragraph from "../atoms/Paragraph";
import ErrorNotice from "../atoms/ErrorNotice";
import { useTranslation } from "react-i18next";
import { getAppThemeName } from "../../../services/appTheme";

const StyledErrorNotice = styled(ErrorNotice)`
  margin-bottom: 20px;
`;

const StyledInputRadio = styled(InputRadio)`
  display: block;
  margin-bottom: 12px;

  input {
    visibility: hidden;

    &:checked + label {
      background-color: ${props => props.theme.tertiary};
      color: ${color.body.base};
      
      &:before {
        border-color: ${props => props.theme.tertiary};
      }

      &:after {
        background-color: transparent;
      }
    }
  }

  label {
    position: relative;
    display: block;
    border-radius: 3px;
    transition: all 0.2s ease-in-out;
    background-color: ${color.gray.light};
    color: ${props => props.theme.tertiary};
    font-size: 15px;
    padding: 20px 20px 20px 48px;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);

    @media only screen and (min-width: 540px) {
      font-size: 18px;
      padding: 20px 20px 20px 56px;
    }

    &:before {
      transition: border-color 0.2s ease-in-out;
      position: absolute;
      content: "";
      width: 18px;
      height: 18px;
      left: 20px;
      top: 50%;
      transform: translateY(-50%);
      border-radius: 2px;
      background-color: #ffffff;
      border: 1px solid ${props => props.theme.tertiary};
    }

    &:after {
      transition: opacity 0.2s ease-in-out;
      position: absolute;
      content: "";
      width: 14px;
      height: 10px;
      left: 23px;
      top: 50%;
      transform: translateY(-50%);
      border-radius: 0;
      background-repeat: no-repeat;
      background-image: url("${props =>
        props.appTheme === "invoned"
          ? "data:image/svg+xml,%3C%3Fxml version='1.0' encoding='UTF-8'%3F%3E%3Csvg width='80px' height='55px' viewBox='0 0 80 55' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Ctitle%3EPath%3C/title%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='download' fill='%2359256B' fill-rule='nonzero'%3E%3Cpath d='M72.1,0.038 C70.3,0.138 68.6,0.838 67.3,2.138 L31.7,37.738 L12.5,22.638 C9.6,20.138 5.1,20.438 2.6,23.438 C0.1,26.438 0.4,30.838 3.4,33.338 C3.5,33.438 3.7,33.538 3.8,33.638 L27.8,52.638 C30.6,54.838 34.6,54.638 37.1,52.138 L77.1,12.138 C79.9,9.438 80,5.038 77.3,2.238 C75.9,0.738 74,-0.062 72.1,0.038 Z' id='Path'%3E%3C/path%3E%3C/g%3E%3C/g%3E%3C/svg%3E"
          : "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA3OC4zIDU0LjEiPjxwYXRoIGQ9Ik03MS4xLjAzOGMtMS44LjEtMy41LjgtNC44IDIuMWwtMzUuNiAzNS42LTE5LjItMTUuMWMtMi45LTIuNS03LjQtMi4yLTkuOS44cy0yLjIgNy40LjggOS45Yy4xLjEuMy4yLjQuM2wyNCAxOWMyLjggMi4yIDYuOCAyIDkuMy0uNWw0MC00MGMyLjgtMi43IDIuOS03LjEuMi05LjktMS40LTEuNS0zLjMtMi4zLTUuMi0yLjJ6IiBmaWxsPSIjZmY4MzAwIi8+PC9zdmc+"}");
      opacity: 0;
      background-size: contain;
      background-color: transparent;
    }
  }
`;

const StyledInputWrapper = styled.div`
  margin-bottom: 30px;
`;

const StyledInputFirstItem = styled(InputItem)`
  max-width: 240px;

  input {
    padding: 30px 20px 10px 25px;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 30px;

  ${props =>
    props.spaceBetween &&
    css`
      justify-content: space-between;
    `};
`;

const StyledArrowRight = styled(ArrowRight)`
  width: 15px;
  height: 16px;
  fill: white;
`;

const StyledCross = styled(Cross)`
  width: 14px;
  height: 14px;
  fill: white;
`;

const StyledSelectItem = styled(SelectItem)`
  margin-top: 10px;
`;

const PayDebtModal = ({ onRequestClose, select }) => {
  const { t } = useTranslation(["common", "dossier"]);
  const data = useSelector(state => state.data);
  const dispatch = useDispatch();
  const dossier = data.dossier;
  const transactions = data.transactions;

  const [approve, setApprove] = useState(false);
  const [errors, setErrors] = useState({});
  const [payment, setPayment] = useState({
    type: "",
    amount: "",
    loading: false,
  });

  useEffect(() => {
    setPayment({ ...payment, type: select });
  }, []);

  const submit = e => {
    e.preventDefault();
    let newPayment = { ...payment, loading: true };
    setErrors({});

    if (newPayment.type === "full") {
      newPayment.amount = dossier.balance;
    }

    if (newPayment.type === "withoutJudgement") {
      newPayment.amount = dossier.balanceBeforeVerdict;
    }

    setPayment(newPayment);

    dispatch(getRedirectUrl(newPayment))
      .then(response => (window.location.href = response.redirect_uri))
      .catch(err => {
        setPayment({ ...newPayment, loading: false });
        setErrors(err.errors);
      });
  };

  const handleAmountUpdate = e =>
    setPayment({ ...payment, amount: e.target.value });

  const handleTypeUpdate = e =>
    setPayment({ ...payment, type: e.target.value });

  const isPayButtonDisabled =
    !!!payment.type || (payment.type === "partial" && payment.amount < 5);

  const renderPaymentAlert = (
    <>
      <Paragraph>{t("dossier:payment.has_open")}</Paragraph>
      <ButtonWrapper spaceBetween>
        <Button red reversed icon={<StyledCross />} onClick={onRequestClose}>
          {t("generic.no")}
        </Button>
        <Button
          secondary
          reversed
          icon={<StyledArrowRight />}
          onClick={() => setApprove(true)}
        >
          {t("generic.yes")}
        </Button>
      </ButtonWrapper>
    </>
  );

  const renderForm = (
    <>
      <form onSubmit={submit}>
        <StyledInputWrapper>
          <StyledInputRadio
            type="radio"
            id="pay-all"
            name="type"
            value="full"
            label={t("dossier:payment.pay_full", {
              amount: moneyFormat(dossier.balance),
            })}
            disabled={payment.loading ? 1 : 0}
            onChange={handleTypeUpdate}
            checked={payment.type === "full"}
            appTheme={getAppThemeName()}
          />

          {dossier.balanceBeforeVerdict > 0 && (
            <StyledInputRadio
              type="radio"
              id="without-judgement"
              name="type"
              value="withoutJudgement"
              disabled={payment.loading ? 1 : 0}
              label={t("dossier:payment.pay_without_judgment", {
                amount: moneyFormat(dossier.balanceBeforeVerdict),
              })}
              onChange={handleTypeUpdate}
              checked={payment.type === "withoutJudgement"}
              appTheme={getAppThemeName()}
            />
          )}

          <StyledInputRadio
            type="radio"
            id="pay-partial"
            name="type"
            value="partial"
            disabled={payment.loading ? 1 : 0}
            label={t("dossier:payment.pay_partial", {
              min_amount: "€ 5",
            })}
            onChange={handleTypeUpdate}
            checked={payment.type === "partial"}
            appTheme={getAppThemeName()}
          />

          {payment.type === "partial" && (
            <StyledInputFirstItem
              floatingLabel
              variant="money-input"
              type="number"
              label={t("dossier:payment.amount")}
              max={
                dossier.afterVerdict
                  ? dossier.balance + dossier.balanceBeforeVerdict
                  : dossier.balance
              }
              value={payment.amount}
              onChange={handleAmountUpdate}
              disabled={payment.loading ? 1 : 0}
              error={errors.amount}
            />
          )}

          {errors &&
            errors.generic_error && (
              <StyledErrorNotice>{errors.generic_error}</StyledErrorNotice>
            )}
        </StyledInputWrapper>
        <ButtonWrapper>
          <Button
            secondary
            reversed
            icon={<StyledArrowRight />}
            loading={payment.loading ? 1 : 0}
            disabled={isPayButtonDisabled}
          >
            {t("dossier:payment.with_ideal")}
          </Button>
        </ButtonWrapper>
      </form>
    </>
  );

  return (
    <>{transactions.length > 0 && !approve ? renderPaymentAlert : renderForm}</>
  );
};

export default PayDebtModal;
